/**
 * Life Blueprint V1 · Task 3C-6 B3-C3K-6A.
 *
 * Production form-integration + storage transport (pure, UI-agnostic).
 *
 * This module contains the deterministic, testable core of the InfoForm
 * submit flow so it can be validated without rendering React:
 *
 *   - engine-mode read (lb.engineMode: "MOCK" | "V2.9.9")
 *   - V2.9.9 required-input contract validation
 *   - a strict rawInput builder (whitelist-only; strips downstream authority)
 *   - storage persistence for both MOCK and V2.9.9 paths
 *   - the routing decision (which engine handles the submit)
 *
 * ISOLATION GUARANTEES
 *   - Never imports or references generateBlueprint, pipeline.js,
 *     reportViewModel, schema, analysisEngine, interpretationEngine, any Sheet
 *     engine, C3I / C3J / C3K-3 / C3K-5 directly. The production application and
 *     the mock generator are INJECTED by the caller (InfoForm), never imported
 *     here — keeping this module pure and dependency-free.
 *   - No system clock. No Date / Date.now / getFullYear. No default currentGoal
 *     or referenceYear. No mock fallback after a production error.
 *   - No geographic / direction mapping. Sheet15 remains authoritative.
 *   - The V2.9.9 transport payload is EXACTLY the ProductionBlueprintResultV299
 *     (application.report); the full C3K-5 envelope is never stored.
 */

export const STORAGE_KEYS = Object.freeze({
  INPUT: "lb.input",
  RESULT: "lb.result",
  RESULT_V299: "lb.result.v299",
  ENGINE_MODE: "lb.engineMode",
});

export const ENGINE_MODE = Object.freeze({ MOCK: "MOCK", V299: "V2.9.9" });

// The V2.9.9 rawInput contract: exactly these fields are transported.
// birthTimeExact is mandatory and must be a valid HH:MM wall-clock value.
// Legacy birthTime remains reference-only and is never authoritative.
export const V299_REQUIRED_FIELDS = Object.freeze([
  "name", "birthDate", "birthTimeExact", "gender", "region", "currentGoal", "referenceYear",
]);
export const V299_TRANSPORT_FIELDS = Object.freeze([
  "name", "birthDate", "gender", "region", "currentGoal", "referenceYear", "birthTimeExact",
]);
// MOCK legacy required-field behavior (unchanged from pre-existing InfoForm).
export const MOCK_REQUIRED_FIELDS = Object.freeze([
  "name", "birthDate", "birthTime", "gender", "region",
]);

function isNonEmpty(v) { return typeof v === "string" ? v.length > 0 : v !== undefined && v !== null && v !== ""; }

function isValidExactBirthTime(v) {
  if (typeof v !== "string") return false;
  return /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(v.trim());
}

/**
 * Read the current engine mode from storage.
 * B3-C3K-6B2B1 production-default policy: only an explicit "MOCK" opts into the
 * legacy mock engine; "V2.9.9", missing, null, or any unknown/corrupt value all
 * resolve to V2.9.9 (production). Deterministic; no system clock.
 */
export function readEngineMode(storage) {
  let raw = null;
  try { raw = storage && storage.getItem(STORAGE_KEYS.ENGINE_MODE); } catch (_) { raw = null; }
  return raw === ENGINE_MODE.MOCK ? ENGINE_MODE.MOCK : ENGINE_MODE.V299;
}

/**
 * V2.9.9 required-input contract.
 * Exact HH:MM birthTimeExact is required.
 * Legacy two-hour birthTime remains reference-only and is not authoritative.
 */
export function validateProductionFormV299(form) {
  if (form === null || typeof form !== "object" || Array.isArray(form))
    return { ok: false, missing: V299_REQUIRED_FIELDS.slice() };
  const missing = V299_REQUIRED_FIELDS.filter((k) => !isNonEmpty(form[k]));

  if (
    !missing.includes("birthTimeExact") &&
    !isValidExactBirthTime(form.birthTimeExact)
  ) {
    missing.push("birthTimeExact");
  }

  return { ok: missing.length === 0, missing };
}

/** MOCK legacy required-input contract (name/birthDate/birthTime/gender/region). */
export function validateMockForm(form) {
  if (form === null || typeof form !== "object" || Array.isArray(form))
    return { ok: false, missing: MOCK_REQUIRED_FIELDS.slice() };
  const missing = MOCK_REQUIRED_FIELDS.filter((k) => !isNonEmpty(form[k]));
  return { ok: missing.length === 0, missing };
}

/**
 * Build the V2.9.9 rawInput from the customer form.
 * Whitelist-only: exactly V299_TRANSPORT_FIELDS. Any caller-supplied downstream
 * authority (audited / coreOutput / rankingInputs / indices / topIndustry /
 * topRole / etc.) and the legacy 时辰 (birthTime) are structurally dropped.
 * birthTimeExact is copied VERBATIM (blank stays blank; never inferred from
 * birthTime; no minute inference; no 2-hour 时辰 conversion).
 */
export function buildProductionRawInputV299(form) {
  const src = (form !== null && typeof form === "object" && !Array.isArray(form)) ? form : {};
  return {
    name: src.name,
    birthDate: src.birthDate,
    gender: src.gender,
    region: src.region,
    currentGoal: src.currentGoal,
    referenceYear: src.referenceYear,
    birthTimeExact: typeof src.birthTimeExact === "string" ? src.birthTimeExact : "",
  };
}

function safeSet(storage, key, value) {
  try { storage.setItem(key, value); } catch (_) { /* ignore quota */ }
}

const V299_PERSISTENCE_KEYS = Object.freeze([
  STORAGE_KEYS.INPUT,
  STORAGE_KEYS.RESULT_V299,
  STORAGE_KEYS.ENGINE_MODE,
]);

function assertStorageContract(storage) {
  if (!storage || typeof storage.getItem !== "function" ||
      typeof storage.setItem !== "function" || typeof storage.removeItem !== "function") {
    throw new Error("production storage unavailable");
  }
}

function makePersistenceError(cause, { rollbackAttempted = false, rollbackComplete = null } = {}) {
  const detail = cause && cause.message ? cause.message : String(cause || "unknown storage failure");
  const error = new Error(`production persistence failed: ${detail}`);
  error.code = "V299_PERSISTENCE_ERROR";
  error.cause = cause;
  error.rollbackAttempted = rollbackAttempted;
  error.rollbackComplete = rollbackComplete;
  return error;
}

function snapshotProductionState(storage) {
  const snapshot = {};
  for (const key of V299_PERSISTENCE_KEYS) snapshot[key] = storage.getItem(key);
  return snapshot;
}

function restoreProductionSnapshot(storage, snapshot) {
  let complete = true;
  for (const key of V299_PERSISTENCE_KEYS) {
    try {
      const previous = snapshot[key];
      if (previous === null || previous === undefined) storage.removeItem(key);
      else storage.setItem(key, previous);
    } catch (_) {
      complete = false;
    }
  }
  return complete;
}

/**
 * Persist a successful V2.9.9 submit:
 *   - lb.input        ← raw customer form (verbatim)
 *   - lb.result.v299  ← ProductionBlueprintResultV299 (report ONLY)
 *   - lb.engineMode   ← "V2.9.9"
 * MUST NOT touch lb.result.
 */
export function persistProductionResultV299(storage, { form, report }) {
  let serializedInput;
  let serializedReport;
  try {
    serializedInput = JSON.stringify(form);
    serializedReport = JSON.stringify(report);
    if (typeof serializedInput !== "string" || typeof serializedReport !== "string")
      throw new Error("production payload is not serializable");
  } catch (error) {
    throw makePersistenceError(error);
  }

  let snapshot;
  let writesStarted = false;
  try {
    assertStorageContract(storage);
    snapshot = snapshotProductionState(storage);

    writesStarted = true;
    storage.setItem(STORAGE_KEYS.INPUT, serializedInput);
    storage.setItem(STORAGE_KEYS.RESULT_V299, serializedReport);
    storage.setItem(STORAGE_KEYS.ENGINE_MODE, ENGINE_MODE.V299);

    const expected = {
      [STORAGE_KEYS.INPUT]: serializedInput,
      [STORAGE_KEYS.RESULT_V299]: serializedReport,
      [STORAGE_KEYS.ENGINE_MODE]: ENGINE_MODE.V299,
    };

    for (const key of V299_PERSISTENCE_KEYS) {
      const actual = storage.getItem(key);
      if (actual !== expected[key])
        throw new Error(`production persistence verification failed for ${key}`);
    }

    return { ok: true };
  } catch (error) {
    const rollbackAttempted = !!snapshot && writesStarted;
    const rollbackComplete = rollbackAttempted
      ? restoreProductionSnapshot(storage, snapshot)
      : null;

    throw makePersistenceError(error, {
      rollbackAttempted,
      rollbackComplete,
    });
  }
}

/**
 * Persist a successful MOCK submit:
 *   - lb.input      ← raw customer form
 *   - lb.result     ← legacy mock result
 *   - lb.engineMode ← "MOCK"
 * MUST NOT touch lb.result.v299.
 */
export function persistMockResult(storage, { form, result }) {
  safeSet(storage, STORAGE_KEYS.INPUT, JSON.stringify(form));
  safeSet(storage, STORAGE_KEYS.RESULT, JSON.stringify(result));
  safeSet(storage, STORAGE_KEYS.ENGINE_MODE, ENGINE_MODE.MOCK);
}

function looksLikeProductionReport(report) {
  return report !== null && typeof report === "object" && !Array.isArray(report) &&
    report.source === "VALIDATED_V299" && report.engineVersion === "V2.9.9" &&
    report.provenance && report.provenance.module === "PRODUCTION_REPORT_ADAPTER";
}

/**
 * Route an InfoForm submit by engine mode. The production application
 * (`runApplication`) and the legacy mock generator (`runMock`) are INJECTED —
 * this keeps engine wiring out of this pure module.
 *
 * @returns a discriminated outcome:
 *   { engine, ok:true, report }                          // V2.9.9 success
 *   { engine, ok:true, result }                          // MOCK success
 *   { engine, ok:false, reason:"MISSING_FIELDS", missing }
 *   { engine, ok:false, reason:"PRODUCTION_ERROR", error, errorMessage }
 *
 * Fail-closed: on a production error nothing is persisted, no navigation is
 * signalled (ok:false), and the mock generator is NEVER invoked.
 */
export function routeInfoFormSubmit({ mode, form, runApplication, runMock, storage }) {
  if (mode === ENGINE_MODE.V299) {
    const v = validateProductionFormV299(form);
    if (!v.ok) return { engine: ENGINE_MODE.V299, ok: false, reason: "MISSING_FIELDS", missing: v.missing };

    const rawInput = buildProductionRawInputV299(form);
    let application;
    try {
      application = runApplication(rawInput);
    } catch (error) {
      // Fail-closed: no persistence, no navigation, NO mock fallback.
      return { engine: ENGINE_MODE.V299, ok: false, reason: "PRODUCTION_ERROR", error, errorMessage: error && error.message };
    }
    const report = application && application.report;
    if (!looksLikeProductionReport(report)) {
      return { engine: ENGINE_MODE.V299, ok: false, reason: "PRODUCTION_ERROR", error: new Error("production report missing or malformed"), errorMessage: "production report missing or malformed" };
    }
    try {
      persistProductionResultV299(storage, { form, report });
    } catch (error) {
      return {
        engine: ENGINE_MODE.V299,
        ok: false,
        reason: "PERSISTENCE_ERROR",
        error,
        errorMessage: error && error.message,
        rollbackAttempted: !!(error && error.rollbackAttempted),
        rollbackComplete: error && error.rollbackComplete,
      };
    }
    return { engine: ENGINE_MODE.V299, ok: true, report };
  }

  // MOCK route — legacy behavior preserved.
  const v = validateMockForm(form);
  if (!v.ok) return { engine: ENGINE_MODE.MOCK, ok: false, reason: "MISSING_FIELDS", missing: v.missing };
  const result = runMock(form);
  persistMockResult(storage, { form, result });
  return { engine: ENGINE_MODE.MOCK, ok: true, result };
}

/**
 * Browser localStorage adapter.
 * Production persistence failures must remain observable so the V2.9.9
 * transaction can fail closed. Legacy MOCK writes remain best-effort because
 * persistMockResult() still routes its writes through safeSet().
 */
export function createBrowserStorage() {
  return {
    getItem(k) {
      if (typeof localStorage === "undefined") throw new Error("localStorage unavailable");
      return localStorage.getItem(k);
    },
    setItem(k, v) {
      if (typeof localStorage === "undefined") throw new Error("localStorage unavailable");
      localStorage.setItem(k, v);
    },
    removeItem(k) {
      if (typeof localStorage === "undefined") throw new Error("localStorage unavailable");
      localStorage.removeItem(k);
    },
  };
}

// The production-native top-level sections a validated report must carry.
export const PRODUCTION_NATIVE_SECTIONS = Object.freeze([
  "profile", "businessContext", "core", "indices", "career",
  "strategy", "annualAdvice", "directionAdvice", "numberAdvice",
  "actionPlan90Day", "wealthBlueprint",
]);

function isObject(v) { return v !== null && typeof v === "object" && !Array.isArray(v); }

/**
 * PURE reader for the stored ProductionBlueprintResultV299 (B3-C3K-6B1).
 * Reads ONLY lb.result.v299 and validates the authoritative contract.
 * Never imports engines, never re-runs the application, never normalizes,
 * repairs, synthesizes, regenerates from lb.input, uses the system clock,
 * DEFAULT_INPUT, or falls back to MOCK.
 *
 * @returns { ok:true, report } | { ok:false, reason:"MISSING"|"MALFORMED"|"INVALID_CONTRACT" }
 */
export function readStoredProductionResultV299(storage) {
  let raw = null;
  try { raw = storage && storage.getItem(STORAGE_KEYS.RESULT_V299); } catch (_) { raw = null; }
  if (raw === null || raw === undefined || raw === "") return { ok: false, reason: "MISSING" };

  let report;
  try { report = JSON.parse(raw); } catch (_) { return { ok: false, reason: "MALFORMED" }; }
  if (!isObject(report)) return { ok: false, reason: "MALFORMED" };

  if (report.source !== "VALIDATED_V299") return { ok: false, reason: "INVALID_CONTRACT" };
  if (report.engineVersion !== "V2.9.9") return { ok: false, reason: "INVALID_CONTRACT" };
  if (!isObject(report.provenance)) return { ok: false, reason: "INVALID_CONTRACT" };
  if (report.provenance.module !== "PRODUCTION_REPORT_ADAPTER") return { ok: false, reason: "INVALID_CONTRACT" };

  for (const section of PRODUCTION_NATIVE_SECTIONS) {
    if (!isObject(report[section])) return { ok: false, reason: "INVALID_CONTRACT" };
  }
  return { ok: true, report };
}
